import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { DelKnapp } from "./DelKnapp";
import { Percent, ExternalLink, TrendingUp, Building2 } from "lucide-react";
import { ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";
import { endringsLinjer } from "@/components/endringMerke";
import { formatNumber, dec, num } from "@/lib/formatters";
import { tooltipStyle } from "./oversiktConstants";
import { Rentesjokk } from "./Rentesjokk";

const mill = (v) => (v == null ? "—" : `${formatNumber(Math.round(v / 1000))} mill. kr`);
const kr = (v) => (v == null ? "—" : `${formatNumber(Math.round(v))} kr`);
const pst = (v) => (v == null ? "—" : `${dec(v, 2)} %`);

const Kort = ({ tittel, verdi, under, ekstra, testId, farge = "text-[#1F2A36]" }) => (
  <div className="p-3 border border-[#E6E9F0] rounded-lg" data-testid={testId}>
    <p className="text-xs text-[#8AA4BF]">{tittel}</p>
    <p className={`text-2xl font-light ${farge}`}>{verdi}</p>
    {under && <p className="text-[11px] text-[#4B647F]">{under}</p>}
    {ekstra && <p className="text-[11px] text-[#3F8A86] mt-1">{ekstra}</p>}
  </div>
);

const Rangering = ({ rang, tittel, format, testId, gruppeOrd = "fylket" }) => {
  if (!rang) return null;
  return (
    <p className="text-xs text-[#4B647F]" data-testid={testId}>
      {tittel}: nr. <span className="font-semibold text-[#103766]">{rang.own_rank}</span> av {rang.count} i {gruppeOrd} (høgast: {rang.hoegast.name}{" "}
      {format(rang.hoegast.value)} · lågast: {rang.lagast.name} {format(rang.lagast.value)} · median {format(rang.median)})
    </p>
  );
};

export const FinanskostnadSection = ({ data, kommuneCode }) => {
  const latest = data?.latest;
  if (!latest) return null;
  const { history = [], fylke, land, rangering = {}, fylke_name, gjeldsvekst } = data;
  const gruppe = data.gruppe || fylke;
  const gruppeLabel = data.gruppe_namn ? "Storbyane" : "Fylket";
  const utanFylke = !gruppe;
  const gruppeOrd = data.gruppe_namn ? "storbygruppa" : "fylket";
  const fylkeKey = data.gruppe_namn ? "Snitt for storbyane" : `Fylkessnitt (${data.gruppe_namn || fylke_name || "fylket"})`;
  const forste = history[0];

  const chart = history.map((r) => ({
    year: r.year,
    Renteutgifter: r.renteutgifter,
    Avdrag: r.avdrag,
    "Rente av lånegjeld": r.rente_pct,
  }));
  const byFylke = Object.fromEntries(((data.gruppe_history || data.fylke_history) || []).map((r) => [r.year, r]));
  const byLand = Object.fromEntries((data.land_history || []).map((r) => [r.year, r]));
  const gjeldChart = history.map((r) => ({
    year: r.year,
    Kommunekassa: r.laanegjeld_per_innb,
    Konsernet: r.laanegjeld_konsern_per_innb,
    "Landssnitt konsern": byLand[r.year]?.laanegjeld_konsern_per_innb ?? null,
  }));
  const harKonsern = history.some((r) => r.laanegjeld_konsern_per_innb != null);

  const renteChart = history.map((r) => ({
    year: r.year,
    Kommunen: r.rente_pct,
    ...(utanFylke ? {} : { [fylkeKey]: byFylke[r.year]?.rente_pct ?? null }),
    Landssnitt: byLand[r.year]?.rente_pct ?? null,
    "Netto renter": r.netto_rente_pct,
  }));

  return (
    <Card className="bg-white border border-[#E6E9F0] rounded-lg mb-8" data-testid="finanskostnad-card">
      <CardHeader className="border-b border-[#E6E9F0] pb-4">
        <div className="flex items-start justify-between gap-3">
          <CardTitle className="text-lg font-medium text-[#1F2A36] flex items-center gap-2">
            <Percent className="w-5 h-5 text-[#103766]" />
            Finanskostnader – kva rentene kostar kommunen
          </CardTitle>
          {kommuneCode && <DelKnapp kommuneCode={kommuneCode} kommuneNamn={data.kommune_name} tema="gjeld" testId="del-gjeld" />}
        </div>
        <p className="text-sm text-[#4B647F]">
          Renteutgifter og avdrag i kroner, og kor mange prosent av lånegjelda kommunen faktisk betaler i rente. Lånegjeld er langsiktig gjeld
          utanom pensjonsforpliktingar, som ikkje ber renter.
        </p>
      </CardHeader>
      <CardContent className="pt-6 space-y-6">
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-3">
          <Kort
            testId="finans-renteutgifter"
            tittel={`Renteutgifter ${latest.year}`}
            verdi={mill(latest.renteutgifter)}
            under={`${kr(latest.rente_per_innb)} per innbyggjar`}
            ekstra={forste && forste.year !== latest.year ? `${mill(forste.renteutgifter)} i ${forste.year}` : null}
          />
          <Kort
            testId="finans-rentesats"
            tittel="Rente av lånegjelda"
            verdi={pst(latest.rente_pct)}
            under={`av ${mill(latest.laanegjeld)} i lånegjeld`}
            ekstra={utanFylke ? `Landet: ${pst(land?.rente_pct)}` : `${gruppeLabel}: ${pst(gruppe?.rente_pct)} · Landet: ${pst(land?.rente_pct)}`}
            farge={latest.rente_pct != null && land?.rente_pct != null && latest.rente_pct > land.rente_pct ? "text-[#C0392B]" : "text-[#1F2A36]"}
          />
          <Kort
            testId="finans-netto"
            tittel="Netto renteutgifter"
            verdi={mill(latest.netto_renteutgifter)}
            under={`etter ${mill(latest.renteinntekter)} i renteinntekter og ${mill(latest.utbytte)} i utbytte`}
            ekstra={`${pst(latest.netto_rente_pct)} av lånegjelda`}
          />
          <Kort
            testId="finans-avdrag"
            tittel="Avdrag på lån"
            verdi={mill(latest.avdrag)}
            under={`${pst(latest.avdrag_pct)} av lånegjelda i året`}
            ekstra={latest.nedbetalingstid ? `Nedbetalingstid i dette tempoet: ${dec(latest.nedbetalingstid, 1)} år` : null}
          />
          <Kort
            testId="finans-sum"
            tittel="Renter og avdrag i alt"
            verdi={mill(latest.rente_og_avdrag)}
            under={`${kr(latest.rente_og_avdrag_per_innb)} per innbyggjar`}
            ekstra={`${pst(latest.rente_og_avdrag_pct_driftsinntekter)} av driftsinntektene`}
          />
        </div>

        {latest.renteeffekt && (
          <div className="p-4 border border-[#E8A34A]/40 bg-[#FDF8F0] rounded-lg" data-testid="finans-renteeffekt">
            <p className="text-xs font-semibold uppercase tracking-[0.1em] text-[#8A7340] mb-1">Kva kostar høgare rente?</p>
            <p className="text-sm text-[#4B647F] mb-3">
              Rekna på lånegjelda i {latest.year} ({mill(latest.laanegjeld)}). Mange kommunar har delar av gjelda bunden eller rentesikra, så dette
              er kostnaden når heile lånegjelda får den nye renta.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {latest.renteeffekt.map((e) => (
                <div key={e.pp} className="p-3 bg-white border border-[#E6E9F0] rounded-lg" data-testid={`finans-renteeffekt-${e.pp}`}>
                  <p className="text-xs text-[#8AA4BF]">+{e.pp} prosentpoeng rente</p>
                  <p className="text-2xl font-light text-[#C0392B]">{mill(e.kr)}</p>
                  <p className="text-[11px] text-[#4B647F]">
                    {kr(e.per_innb)} per innbyggjar · {pst(e.pct_driftsinntekter)} av driftsinntektene
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}

        {gjeldsvekst && (
          <div
            className={`p-4 rounded-lg border ${
              gjeldsvekst.varsel ? "border-[#E52437]/40 bg-[#FFF4F5]" : "border-[#E6E9F0] bg-[#FAFBFC]"
            }`}
            data-testid="finans-gjeldsvarsel"
          >
            <p
              className={`text-xs font-semibold uppercase tracking-[0.1em] mb-1 flex items-center gap-1.5 ${
                gjeldsvekst.varsel ? "text-[#C0392B]" : "text-[#8AA4BF]"
              }`}
            >
              <TrendingUp className="w-3.5 h-3.5" />
              {gjeldsvekst.varsel ? "Gjeldsvarsel" : "Utvikling i lånegjelda"}
            </p>
            <p className="text-sm text-[#4B647F]">
              Lånegjelda per innbyggjar gjekk frå <span className="font-semibold text-[#1F2A36]">{kr(gjeldsvekst.fra)}</span> i{" "}
              {gjeldsvekst.fra_aar} til{" "}
              <span className={`font-semibold ${gjeldsvekst.varsel ? "text-[#C0392B]" : "text-[#1F2A36]"}`}>{kr(gjeldsvekst.til)}</span> i{" "}
              {gjeldsvekst.til_aar} – ein vekst på{" "}
              <span className={`font-semibold ${gjeldsvekst.varsel ? "text-[#C0392B]" : "text-[#1F2A36]"}`}>
                {dec(gjeldsvekst.vekst_pct, 1)} %
              </span>{" "}
              ({kr(gjeldsvekst.endring_per_innb)} per innbyggjar)
              {gjeldsvekst.varsel ? ". Det er over 50 prosent, og veksten er merkt som eit varsel." : "."}
            </p>
          </div>
        )}

        {harKonsern && (
          <div className="p-4 border border-[#E6E9F0] rounded-lg" data-testid="finans-konsern">
            <p className="text-xs font-semibold uppercase tracking-[0.1em] text-[#8AA4BF] mb-1 flex items-center gap-1.5">
              <Building2 className="w-3.5 h-3.5" />
              Gjeld i kommunekonsernet
            </p>
            <p className="text-sm text-[#4B647F] mb-3">
              Kommunekassa viser berre gjelda kommunen sjølv har i balansen. Konsernet tek òg med kommunale føretak (KF) og kommunen sin del av
              interkommunale selskap (IKS), der mykje av gjelda til vatn, avløp, bustader og bygg ofte ligg.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <Kort
                testId="finans-konsern-kasse"
                tittel={`Kommunekassa ${latest.year}`}
                verdi={kr(latest.laanegjeld_per_innb)}
                under={`${mill(latest.laanegjeld)} i lånegjeld`}
              />
              <Kort
                testId="finans-konsern-konsern"
                tittel={`Konsernet ${latest.year}`}
                verdi={kr(latest.laanegjeld_konsern_per_innb)}
                under={`${mill(latest.laanegjeld_konsern)} i lånegjeld`}
                farge="text-[#C0392B]"
                ekstra={land?.laanegjeld_konsern_per_innb ? `Landet: ${kr(land.laanegjeld_konsern_per_innb)}` : null}
              />
              <Kort
                testId="finans-konsern-tillegg"
                tittel="Tillegg i føretak og IKS"
                verdi={kr(latest.konsern_tillegg_per_innb)}
                under={
                  latest.laanegjeld && latest.laanegjeld_konsern
                    ? `${dec((latest.laanegjeld_konsern / latest.laanegjeld - 1) * 100, 1)} % meir enn kommunekassa`
                    : null
                }
              />
            </div>
            <div className="h-[260px] mt-4" data-testid="finans-konsern-chart">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={gjeldChart}>
                  {endringsLinjer(gjeldChart)}
                  <CartesianGrid strokeDasharray="3 3" stroke="#E6E9F0" />
                  <XAxis dataKey="year" stroke="#8AA4BF" fontSize={11} />
                  <YAxis stroke="#8AA4BF" fontSize={11} width={62} tickFormatter={(v) => `${num(v / 1000, 0)}k`} />
                  <Tooltip contentStyle={tooltipStyle} formatter={(v) => kr(v)} />
                  <Legend wrapperStyle={{ fontSize: 11 }} iconSize={9} />
                  <Line type="monotone" dataKey="Kommunekassa" stroke="#103766" strokeWidth={2.5} dot={{ r: 3 }} connectNulls />
                  <Line type="monotone" dataKey="Konsernet" stroke="#C0392B" strokeWidth={2.5} dot={{ r: 3 }} connectNulls />
                  <Line type="monotone" dataKey="Landssnitt konsern" stroke="#9FB6CC" strokeWidth={1.5} dot={false} connectNulls />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        <Rentesjokk data={data} />

        <div className="space-y-1">
          <Rangering gruppeOrd={gruppeOrd} rang={rangering.rente_pct} tittel="Rente av lånegjelda" format={(v) => `${dec(v, 2)} %`} testId="finans-rang-rente" />
          <Rangering gruppeOrd={gruppeOrd} rang={rangering.laanegjeld_per_innb} tittel="Lånegjeld per innbyggjar" format={(v) => `${formatNumber(v)} kr`} testId="finans-rang-gjeld" />
          <Rangering
            gruppeOrd={gruppeOrd}
            rang={rangering.laanegjeld_konsern_per_innb}
            tittel="Lånegjeld i konsernet per innbyggjar"
            format={(v) => `${formatNumber(v)} kr`}
            testId="finans-rang-gjeld-konsern"
          />
          <Rangering
            gruppeOrd={gruppeOrd}
            rang={rangering.rente_og_avdrag_per_innb}
            tittel="Renter og avdrag per innbyggjar"
            format={(v) => `${formatNumber(v)} kr`}
            testId="finans-rang-sum"
          />
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.1em] text-[#8AA4BF] mb-2">Renter og avdrag i kroner, med rentesatsen</p>
            <div className="h-[280px]" data-testid="finans-chart">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={chart} barCategoryGap="30%">
                  {endringsLinjer(chart, { yAxisId: "kr" })}
                  <CartesianGrid strokeDasharray="3 3" stroke="#E6E9F0" />
                  <XAxis dataKey="year" stroke="#8AA4BF" fontSize={11} />
                  <YAxis yAxisId="kr" stroke="#8AA4BF" fontSize={11} width={62} tickFormatter={(v) => `${num(v / 1000, 0)} mill.`} />
                  <YAxis yAxisId="pct" orientation="right" stroke="#C0392B" fontSize={11} width={44} unit=" %" domain={[0, (max) => Math.ceil(max * 1.5)]} />
                  <Tooltip contentStyle={tooltipStyle} formatter={(v, n) => (n === "Rente av lånegjeld" ? pst(v) : mill(v))} />
                  <Legend wrapperStyle={{ fontSize: 11 }} iconSize={9} />
                  <Bar yAxisId="kr" dataKey="Renteutgifter" stackId="f" fill="#103766" />
                  <Bar yAxisId="kr" dataKey="Avdrag" stackId="f" fill="#9FB6CC" radius={[4, 4, 0, 0]} />
                  <Line yAxisId="pct" type="monotone" dataKey="Rente av lånegjeld" stroke="#C0392B" strokeWidth={2.5} dot={{ r: 3 }} />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.1em] text-[#8AA4BF] mb-2">Rentesatsen mot {utanFylke ? "landet" : data.gruppe_namn ? "storbyane og landet" : "fylket og landet"}</p>
            <div className="h-[280px]" data-testid="finans-rente-chart">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={renteChart}>
                  {endringsLinjer(renteChart)}
                  <CartesianGrid strokeDasharray="3 3" stroke="#E6E9F0" />
                  <XAxis dataKey="year" stroke="#8AA4BF" fontSize={11} />
                  <YAxis stroke="#8AA4BF" fontSize={11} width={44} unit=" %" />
                  <Tooltip contentStyle={tooltipStyle} formatter={(v) => pst(v)} />
                  <Legend wrapperStyle={{ fontSize: 10 }} iconSize={8} />
                  <Line type="monotone" dataKey="Kommunen" stroke="#103766" strokeWidth={2.5} dot={{ r: 3 }} connectNulls />
                  {!utanFylke && <Line type="monotone" dataKey={fylkeKey} stroke="#3F8A86" strokeWidth={1.5} dot={false} connectNulls />}
                  <Line type="monotone" dataKey="Landssnitt" stroke="#9FB6CC" strokeWidth={1.5} dot={false} connectNulls />
                  <Line type="monotone" dataKey="Netto renter" stroke="#E8A34A" strokeWidth={1.5} strokeDasharray="4 3" dot={false} connectNulls />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {forste && forste.year !== latest.year && forste.rente_pct != null && (
          <p className="text-sm text-[#4B647F]" data-testid="finans-utvikling-tekst">
            Frå {forste.year} til {latest.year} gjekk renta kommunen betaler frå{" "}
            <span className="font-semibold text-[#1F2A36]">{pst(forste.rente_pct)}</span> til{" "}
            <span className="font-semibold text-[#C0392B]">{pst(latest.rente_pct)}</span> av lånegjelda, og renteutgiftene frå{" "}
            {mill(forste.renteutgifter)} til {mill(latest.renteutgifter)} i året – ein endring på{" "}
            {mill(latest.renteutgifter - forste.renteutgifter)}, eller{" "}
            {forste.rente_per_innb != null && latest.rente_per_innb != null
              ? `${kr(latest.rente_per_innb - forste.rente_per_innb)} per innbyggjar`
              : "—"}
            .
          </p>
        )}

        <p className="text-xs text-[#8AA4BF]">
          Kjelder: SSB{" "}
          <a href="https://www.ssb.no/statbank/table/13543" target="_blank" rel="noopener noreferrer" className="underline inline-flex items-center gap-1">
            13543 <ExternalLink className="w-3 h-3" />
          </a>{" "}
          (økonomisk oversikt drift: renteutgifter, renteinntekter, utbytte og avdrag) og{" "}
          <a href="https://www.ssb.no/statbank/table/13545" target="_blank" rel="noopener noreferrer" className="underline inline-flex items-center gap-1">
            13545 <ExternalLink className="w-3 h-3" />
          </a>{" "}
          (balanse, både kommunekassa og den konsoliderte konsernrekneskapen). Hovudtala gjeld kommunekassa, same grunnlag som arbeidskapital og
          fond elles i appen, og finst frå 2020. Lånegjeld = langsiktig
          gjeld minus pensjonsforpliktingar. Renta er ein implisitt rentesats: renteutgifter delt på lånegjelda ved årsslutt, så nye lån tekne opp
          sent i året kan trekkje satsen litt ned. {utanFylke ? "Landssnittet er" : "Fylkes- og landssnitta er"} vekta, det vil seie summen av kroner delt på summen av gjeld. {utanFylke ? "Oslo er både kommune og fylke, så vi viser ikkje noko fylkessnitt." : ""}
        </p>
      </CardContent>
    </Card>
  );
};

export default FinanskostnadSection;
